import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, shareReplay, tap } from 'rxjs';
import { ConfigService } from './config.service';
import { ApiImage } from '../models/image-api.models';
import { Announcement } from '../models/public.models';
import { ApiConfigService } from './api-config.service';

@Injectable({ providedIn: 'root' })
export class ImageApiService {
  private cachedList$: Observable<ApiImage[]> | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly apiConfig: ApiConfigService,
    private readonly configService: ConfigService,
  ) {}

  /** GET /api-produit{suffixe}/image/findall */
  list(forceRefresh = false): Observable<ApiImage[]> {
    const url = this.apiConfig.imageListUrl;

    if (!this.cachedList$ || forceRefresh) {
      this.cachedList$ = this.http.get<ApiImage[]>(url).pipe(
        map((items) => items.filter((item) => this.isValidUrl(item.url))),
        tap((response) => {
          if (this.configService.enableApiLogs) {
            console.log('[ImageApi] list', url, response);
          }
        }),
        shareReplay(1)
      );
    }

    return this.cachedList$;
  }

  getByName(name: string): Observable<ApiImage | undefined> {
    return this.list().pipe(
      map((items) =>
        items
          .filter((item) => item.name === name)
          .sort((a, b) => this.dateValue(b.updatedAt) - this.dateValue(a.updatedAt))[0]
      )
    );
  }

  getUrlByName(name: string, fallback = ''): Observable<string> {
    return this.getByName(name).pipe(map((item) => item?.url || fallback));
  }

  /** Slides hero : images dont le nom commence par ACCUEIL_CAROUSSEL */
  getCarouselAnnouncements(): Observable<Announcement[]> {
    const prefix = 'accueil_caroussel';

    return this.list().pipe(
      map((items) => {
        const bySlot = new Map<number, ApiImage>();

        for (const item of items) {
          const name = (item.name || '').toLowerCase().trim();
          if (!name.startsWith(prefix)) {
            continue;
          }

          const match = /^accueil_caroussel_(\d+)$/i.exec(name);
          const slot = match ? Number(match[1]) : bySlot.size + 1;
          const existing = bySlot.get(slot);

          if (!existing || this.dateValue(item.updatedAt) >= this.dateValue(existing.updatedAt)) {
            bySlot.set(slot, item);
          }
        }

        return [...bySlot.entries()]
          .sort(([a], [b]) => a - b)
          .map(([slot, item]) => this.toAnnouncement(item, slot));
      })
    );
  }

  clearCache(): void {
    this.cachedList$ = null;
  }

  private toAnnouncement(item: ApiImage, slot: number): Announcement {
    const comment = (item.coment || '')
      .replace(/^<<\s*/, '')
      .replace(/\s*>>$/, '')
      .trim();
    return {
      id: item.id,
      title: comment || this.defaultTitle(slot),
      subtitle: this.defaultSubtitle(slot),
      imageUrl: item.url,
      ctaLabel: 'NOS PRODUITS',
      ctaRoute: '/accueil',
    };
  }

  private defaultTitle(slot: number): string {
    const titles = [
      'Avec SAAR-VIE, vivez en toute quiétude',
      'Protégez votre avenir avec confiance',
      'Des solutions d’assurance pour chaque besoin',
    ];
    return titles[(slot - 1) % titles.length];
  }

  private defaultSubtitle(slot: number): string {
    const subtitles = [
      'Assurance vie, prévoyance et épargne au Cameroun.',
      'Des formules claires, un accompagnement de proximité.',
      'Souscrivez en ligne et gérez vos contrats simplement.',
    ];
    return subtitles[(slot - 1) % subtitles.length];
  }

  private isValidUrl(url: string | null | undefined): boolean {
    return !!url && /^https?:\/\//i.test(url.trim());
  }

  private dateValue(value?: string | null): number {
    if (!value) {
      return 0;
    }
    const time = Date.parse(value);
    return Number.isNaN(time) ? 0 : time;
  }
}
